import { onlineManager, type QueryClient } from '@tanstack/react-query';

import { isOnline } from '../model/connectivity';
import { catchUp } from './catchUp';

/**
 * Вернулась сеть - данные на экране обновляются сами: `catchUp` один раз перечитывает всё, что
 * сейчас на экране. Запросы, вставшие без сети на паузу, react-query продолжает сам.
 *
 * О возвращении узнаём двумя путями. Первый - `onlineManager`: переход из "нет сети" в "есть".
 *
 * Второй закрывает дыру. `onlineManager` стартует с "сеть есть" и узнаёт о пропаже только из
 * события `offline`. Страница, открытая уже без сети, такого события не получает: react-query
 * считает, что сеть есть, запросы падают, а возвращение сети для него не событие - было "есть",
 * стало "есть". Экран так и оставался бы с ошибками, хотя полоса "Нет подключения" уже ушла.
 * Поэтому, если сеть пропадала без ведома react-query, по событию `online` зовём `catchUp` сами.
 *
 * Нужно это вебу. Приложение передаёт стартовое `navigator.onLine` в react-query само
 * (`syncOnlineAtBoot`): там флаг идёт от системы, а паузу, если он всё же соврал, снимает первый
 * же ответ сервера или открытый сокет (`markNetworkProven`). После этого пропажа для react-query
 * уже не тайна, и второй путь молчит. Веб стартовый флаг в react-query не передаёт: у
 * настольного Chromium бывают ложные "нет сети", а анонимному посетителю паузу снять нечем - ни
 * сокета, ни мутаций. Здесь ложное "нет сети" стоит одного лишнего перезапроса, не вечной паузы.
 */
/**
 * Пауза перед перечитыванием экрана: первое возвращение - сразу, повторы - всё позже.
 *
 * 🔴 Паузы не было вовсе, и это стоило шторма. Медленный шлюз ронял чтения по таймауту, полоса
 * «нет связи» загоралась и гасла примерно раз в 45 секунд, и каждое гашение перезапрашивало ВСЁ,
 * что открыто на экране - у каждого клиента, и тем чаще, чем хуже серверу. Сам круг разорван
 * тем, что таймаут больше не объявляет сервер упавшим, но пауза остаётся второй опорой: любое
 * мигание связи не должно превращаться в залп запросов.
 *
 * 🔴 Первое возвращение идёт СРАЗУ и синхронно, и это не забота о человеке, а условие
 * правильности. Проход обязан успеть до того, как react-query сам продолжит вставшие на паузу
 * запросы: тогда его повтор попадает в окно склейки `catchUp` и остаётся ровно один запрос.
 * Отложенный хотя бы на нулевой таймер проход приезжает уже после - и экран читается дважды,
 * то есть ровно тот залп, от которого пауза и заводилась (поймано тестами этого файла).
 *
 * Платят за шторм только повторы.
 */
const CATCH_UP_DELAYS_MS = [2_000, 5_000, 15_000] as const;

/** Столько тишины - и следующее возвращение снова считается первым. */
const CALM_AFTER_MS = 60_000;

export const installReconnectCatchUp = (queryClient: QueryClient): (() => void) => {
  let offlineUnseen = !isOnline() && onlineManager.isOnline();
  let repeats = 0;
  let lastCatchUpAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  /** Одна очередь на оба пути: два сигнала подряд дают одно перечитывание, а не два. */
  const scheduleCatchUp = () => {
    if (timer !== undefined) {
      return;
    }

    const now = Date.now();
    if (lastCatchUpAt !== 0 && now - lastCatchUpAt >= CALM_AFTER_MS) {
      repeats = 0;
    }

    if (repeats === 0) {
      repeats = 1;
      lastCatchUpAt = now;
      catchUp(queryClient, 'online');
      return;
    }

    const delay = CATCH_UP_DELAYS_MS[Math.min(repeats - 1, CATCH_UP_DELAYS_MS.length - 1)];
    repeats += 1;

    timer = setTimeout(() => {
      timer = undefined;
      lastCatchUpAt = Date.now();
      // Пока ждали, сеть могла пропасть снова - тогда перечитывать нечего, запросы всё равно
      // встанут на паузу, а их провалы зря засчитаются в недоступность.
      if (onlineManager.isOnline()) {
        catchUp(queryClient, 'online');
      }
    }, delay);
  };

  const unsubscribe = onlineManager.subscribe((online) => {
    if (online) {
      scheduleCatchUp();
    } else {
      offlineUnseen = false;
    }
  });

  const onOffline = () => {
    // Слушатель react-query может сработать и до нас, и после: во втором случае подписка выше
    // тут же вернёт флаг обратно.
    offlineUnseen = onlineManager.isOnline();
  };

  const onOnline = () => {
    if (!offlineUnseen) {
      return;
    }
    offlineUnseen = false;
    scheduleCatchUp();
  };

  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  return () => {
    clearTimeout(timer);
    timer = undefined;
    unsubscribe();
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('online', onOnline);
  };
};
